/**
 * The documents client — the checklist, uploads, versions, the share, the ZIP
 * (spec §F).
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **No rule is re-derived here.** Which handling state a document may move to,
 * whether an approval may be asked for, whether a share is still possible — all
 * of it comes from the server, on `vocabulary()`'s transition maps and on the
 * document's own fields, because the server is the thing that will refuse. Two
 * copies of a rule agree today and disagree in a year, and the one the firm sees
 * would be the wrong one — the shape `lib/api/cases.ts` set in Wave 3A.
 *
 * Two things here are not `api()` calls and cannot be:
 *
 * - **`upload`** is `multipart/form-data`. It goes through `session.authed`
 *   directly, because `api()` sets `content-type: application/json` on any
 *   request with a body and a multipart body must carry the browser's own
 *   boundary. It re-implements the org header and the error envelope, which is
 *   the price, and `uploadUrl()` keeps the path in one place.
 * - **`zipUrl`** is a `GET` the browser must perform itself, and it cannot: the
 *   response is a stream behind a bearer and an org header. So `downloadZip`
 *   fetches it authenticated and hands back a `Blob` for the screen to save.
 */

import { ApiError, ORG_HEADER, api, getOrgId } from '../api'
import { SERVICE_API } from '../config'
import { session } from '../session'

// -- shapes ------------------------------------------------------------------

export type RequestState = 'pending' | 'received' | 'rejected' | 'verified'
export type ApprovalStatus = 'not_required' | 'pending' | 'approved' | 'rejected'
export type Handling = 'new' | 'in_progress' | 'done'
/** `booking` — what a visitor sent with a booking, promoted when it was submitted
 *  (C25, ADR-0053 §3). */
export type DocumentSource = 'staff' | 'portal' | 'chat' | 'system' | 'booking'

/** One warning from the wrong-client guardrail (FR-E14).
 *
 *  `message` is written to be shown **verbatim**: whoever is reading it is
 *  mid-upload with a due date, and a code they have to look up is a code they
 *  ignore. Never re-word it here. */
export interface GuardrailWarning {
  code: 'pan_mismatch' | 'name_not_found'
  message: string
  details: Record<string, unknown>
}

export interface Guardrail {
  /** Did we get readable text out of the file at all? `false` is the fail-open
   *  answer and carries no warnings by construction. */
  checked: boolean
  format: string | null
  pan_matched: boolean | null
  name_matched: boolean | null
  /** Masked by the service before it ever reaches here. */
  pans_found: string[]
  warnings: GuardrailWarning[]
}

export interface DocumentRow {
  id: string
  client_id: string
  case_id: string | null
  request_id: string | null
  filename: string
  content_type: string
  size_bytes: number
  checksum_sha256: string | null
  source: DocumentSource
  title: string | null
  notes: string | null
  version: number
  supersedes_id: string | null
  is_current: boolean
  superseded_at: string | null
  approval_status: ApprovalStatus
  approval_requested_at: string | null
  approval_decided_at: string | null
  approval_note: string | null
  /** What the client's approval did to the **case** — FR-F5, T-2.21. `'moved'`,
   *  `'refused'`, or `null` when the stage the case was in declares no
   *  `advance_on_approval`, which is most stages. `approval_advance_note`
   *  carries the target stage key on a move and the engine's own refusal
   *  sentence on a refusal. Staff-only: the portal serves neither. */
  approval_advance: 'moved' | 'refused' | null
  approval_advance_note: string | null
  handling: Handling
  /** **Once set, never cleared** (FR-F4). There is no un-share and there is no
   *  route for one. */
  shared_at: string | null
  shared_by: string | null
  share_note: string | null
  guardrail: Partial<Guardrail>
  /** C25 (ADR-0053 §5) — the booking a visitor sent this with, and its place
   *  in it (`id_card`, `payment_proof`). `null` on every other document; absent
   *  from a service before Wave 27. */
  booking_id?: string | null
  booking_slot?: string | null
  uploaded_by: string | null
  uploaded_at: string | null
}

export interface DocumentRequestRow {
  id: string
  case_id: string
  client_id: string
  checklist_item_id: string | null
  name: string
  is_required: boolean
  accepted_formats: string[]
  help_text: string | null
  display_order: number
  state: RequestState
  reason: string | null
  received_at: string | null
  decided_at: string | null
  /** The current version for this item — computed by the service, never stored
   *  (`models/documents.py`). Rides along so a twelve-item checklist is one
   *  round-trip. */
  document: DocumentRow | null
}

export interface DocumentReadRow {
  id: string
  reader_kind: 'user' | 'person' | 'system'
  reader_user_id: string | null
  purpose: 'view' | 'download' | 'zip' | 'share' | 'portal'
  at: string | null
}

export interface DocumentDetail {
  document: DocumentRow
  /** Every version, newest first. Superseded ones are still readable — that is
   *  what "nothing is deleted" means here (FR-F7). */
  versions: DocumentRow[]
  /** Constitution §4's audit, as a screen can show it. */
  reads: DocumentReadRow[]
}

export interface UploadResult {
  document: DocumentRow
  request: DocumentRequestRow | null
  /** The guardrail's warnings. **The upload already happened** — these are
   *  information, never a refusal (FR-E14). */
  warnings: GuardrailWarning[]
}

export interface DocumentVocabulary {
  request_states: RequestState[]
  request_transitions: Record<RequestState, RequestState[]>
  approval_statuses: ApprovalStatus[]
  approval_transitions: Record<ApprovalStatus, ApprovalStatus[]>
  handling_states: Handling[]
  handling_transitions: Record<Handling, Handling[]>
  sources: DocumentSource[]
  max_upload_bytes: number
  max_zip_documents: number
  can_view_all: boolean
  can_create: boolean
  can_update: boolean
}

export interface DocumentFilters {
  case_id?: string
  client_id?: string
  request_id?: string
  /** C25 — what one booking brought (the booking's record). */
  booking_id?: string
  source?: DocumentSource
  handling?: Handling
  approval_status?: ApprovalStatus
  shared?: boolean
  include_superseded?: boolean
  q?: string
  limit?: number
  offset?: number
}

// -- the client --------------------------------------------------------------

function query(filters: Record<string, unknown>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const text = params.toString()
  return text ? `?${text}` : ''
}

export const documents = {
  vocabulary: () => api<DocumentVocabulary>('/api/v1/documents/vocabulary'),

  list: (filters: DocumentFilters = {}) =>
    api<DocumentRow[]>(`/api/v1/documents${query(filters as Record<string, unknown>)}`),

  read: (id: string) => api<DocumentDetail>(`/api/v1/documents/${id}`),

  /** `request_id` *(C27, §10.19 item 8)* makes a file already on the case the
   *  answer to that checklist item — it becomes `received`, as an upload
   *  against the item would make it. */
  update: (
    id: string,
    body: Partial<{ title: string; notes: string; handling: Handling; request_id: string }>,
  ) => api<DocumentRow>(`/api/v1/documents/${id}`, { method: 'PATCH', body }),

  /**
   * **Irreversible, and behind step-up.** The assertion comes from core-mfa and
   * the service refuses with `STEPUP_REQUIRED` without one — which is a machine
   * code the screen acts on by launching the challenge, not a generic error.
   */
  share: (id: string, stepUpAssertion: string, note?: string) =>
    api<DocumentRow>(`/api/v1/documents/${id}/share`, {
      method: 'POST',
      body: { note },
      stepUpAssertion,
    }),

  /** No `approve` asks the client; `approve` records what they said (FR-F5). */
  askApproval: (id: string) =>
    api<DocumentRow>(`/api/v1/documents/${id}/approval`, { method: 'POST', body: {} }),
  recordApproval: (id: string, approve: boolean, note?: string) =>
    api<DocumentRow>(`/api/v1/documents/${id}/approval`, {
      method: 'POST',
      body: { approve, note },
    }),

  /** A five-minute capability. Use it and drop it — never store it, never put it
   *  on a message. */
  downloadUrl: (id: string, purpose: 'view' | 'download' = 'view') =>
    api<{ url: string; filename: string; content_type: string; expires_in: number }>(
      `/api/v1/documents/${id}/download?purpose=${purpose}`,
    ),

  requests: (filters: { case_id?: string; client_id?: string; outstanding?: boolean } = {}) =>
    api<DocumentRequestRow[]>(
      `/api/v1/documents/requests${query(filters as Record<string, unknown>)}`,
    ),

  addRequest: (body: {
    case_id: string
    name: string
    is_required?: boolean
    help_text?: string
    accepted_formats?: string[]
  }) => api<DocumentRequestRow>('/api/v1/documents/requests', { method: 'POST', body }),

  /** Verify or reject. `received` is refused by the service — an item becomes
   *  received when a file arrives for it, never by a click. */
  setRequestState: (requestId: string, to: 'verified' | 'rejected', reason?: string) =>
    api<DocumentRequestRow>(`/api/v1/documents/requests/${requestId}/state`, {
      method: 'POST',
      body: { to, reason },
    }),

  /** Pull the service's checklist onto this case. Idempotent, so the button can
   *  be pressed twice. */
  syncRequests: (caseId: string) =>
    api<{ created: number; names: string[] }>('/api/v1/documents/requests/sync', {
      method: 'POST',
      body: { case_id: caseId },
    }),

  upload,
  downloadZip,
}

/**
 * Multipart, and therefore not `api()`.
 *
 * `api()` sets `content-type: application/json` on any request with a body; a
 * multipart body needs the browser's own `multipart/form-data; boundary=…`, so
 * the header must be **absent** and let `fetch` fill it in. Setting it by hand
 * is the classic version of this bug and produces a 422 that names no field.
 */
async function upload(input: {
  file: File
  client_id: string
  case_id?: string
  request_id?: string
  title?: string
  notes?: string
  source?: DocumentSource
}): Promise<UploadResult> {
  const orgId = getOrgId()
  if (!orgId) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)

  const form = new FormData()
  form.append('file', input.file)
  form.append('client_id', input.client_id)
  for (const key of ['case_id', 'request_id', 'title', 'notes', 'source'] as const) {
    const value = input[key]
    if (value) form.append(key, value)
  }

  const res = await session.authed(`${SERVICE_API}/api/v1/documents`, {
    method: 'POST',
    // `ORG_HEADER` — the name the estate speaks. A plain object, not a
    // `Headers`: `@fiveox/shell`'s `authed` spreads what it is given and a
    // `Headers` instance spreads to `{}` (see `lib/api.ts`).
    headers: { [ORG_HEADER]: orgId },
    body: form,
  })
  const body = (await res.json().catch(() => null)) as
    | { success: boolean; data: UploadResult; error?: { code: string; message: string } }
    | null
  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'The document could not be uploaded.',
      res.status,
    )
  }
  return body.data
}

/**
 * FR-F8's bulk download, fetched authenticated and handed back as a `Blob`.
 *
 * A plain `<a href>` cannot do it: the route is behind a bearer and the org
 * header, and a browser navigation carries neither.
 */
async function downloadZip(caseId: string): Promise<Blob> {
  const orgId = getOrgId()
  if (!orgId) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
  const res = await session.authed(
    `${SERVICE_API}/api/v1/documents/zip?case_id=${encodeURIComponent(caseId)}`,
    { headers: { [ORG_HEADER]: orgId } },
  )
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string } }
      | null
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'The documents could not be downloaded.',
      res.status,
    )
  }
  return res.blob()
}

// -- pure helpers, so the screens' rules are testable ------------------------

export const REQUEST_STATE_LABELS: Record<RequestState, string> = {
  pending: 'Waiting',
  received: 'Received',
  rejected: 'Rejected',
  verified: 'Verified',
}

export const HANDLING_LABELS: Record<Handling, string> = {
  new: 'New',
  in_progress: 'In progress',
  done: 'Done',
}

export const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  not_required: 'No approval needed',
  pending: 'Waiting on the client',
  approved: 'Approved',
  rejected: 'Rejected by the client',
}

export const SOURCE_LABELS: Record<DocumentSource, string> = {
  staff: 'Uploaded by the firm',
  portal: 'Sent by the client',
  chat: 'From a message',
  system: 'Generated by ProDesk',
  booking: 'From a booking',
}

/**
 * Which handling states the screen may offer.
 *
 * **Read from the server's map, never re-derived.** `vocabulary()` sends the
 * same transition table `domain/documents.py` enforces, so a button that appears
 * is a button the server will honour, and a rule change is one deployment rather
 * than two.
 */
export function handlingOptions(
  vocab: DocumentVocabulary | null,
  current: Handling,
): Handling[] {
  return vocab?.handling_transitions?.[current] ?? []
}

/**
 * What the share control should say and whether it may be pressed.
 *
 * Three states and they are not interchangeable: *already shared* is permanent
 * and the copy must say so, *superseded* means share the current version
 * instead, and *available* is the only one that is a button.
 */
export function shareState(row: DocumentRow | null): {
  enabled: boolean
  label: string
  reason: string | null
} {
  if (!row) return { enabled: false, label: 'Share with client', reason: null }
  if (row.shared_at) {
    return {
      enabled: false,
      label: 'Shared with client',
      reason:
        'Sharing cannot be undone — the client already has this. To correct it, upload a new version and share that.',
    }
  }
  if (!row.is_current) {
    return {
      enabled: false,
      label: 'Share with client',
      reason: 'This version has been superseded. Share the current one instead.',
    }
  }
  return { enabled: true, label: 'Share with client', reason: null }
}

/**
 * What the **ask for approval** control should say and whether it may be
 * pressed — FR-F5, and ACCEPTANCE §3.2.
 *
 * `documents.askApproval` had **no caller**. The portal's Approve button renders
 * on `can_approve`, which the service computes as *shared, current, and pending*
 * — and nothing ever set a document to pending, so the client was never asked
 * and spec §5 item 4 could not be walked. Same shape as §3.1: a working route, a
 * typed client method, and no button.
 *
 * The transitions are read from the **server's** map, exactly as
 * `handlingOptions` reads the handling one, so a control that appears is a
 * control the server will honour. The two preconditions the map does not carry
 * are here as their own sentences, because both are things a person needs told
 * rather than a disabled button with no explanation:
 *
 * - **Not shared yet.** `request_approval` refuses it — *"an approval request
 *   the client cannot see is a case waiting on a client who was never asked"* —
 *   and the order (share, then ask) is not obvious from the screen.
 * - **Superseded.** Asking about a version the client is not looking at.
 */
export function approvalState(
  row: DocumentRow | null,
  vocab: DocumentVocabulary | null,
): { enabled: boolean; label: string; reason: string | null } {
  if (!row) return { enabled: false, label: 'Ask for approval', reason: null }

  const allowed = vocab?.approval_transitions?.[row.approval_status] ?? []
  const askable = allowed.includes('pending')

  if (row.approval_status === 'pending') {
    return {
      enabled: false,
      label: 'Waiting on the client',
      reason: 'The client has been asked and has not answered yet.',
    }
  }
  if (row.approval_status === 'approved') {
    return {
      enabled: false,
      label: 'Approved by the client',
      reason: 'Approval is a decision the client made; it is not asked again.',
    }
  }
  if (!row.shared_at) {
    return {
      enabled: false,
      label: 'Ask for approval',
      reason: 'Share it with the client first — they cannot approve what they cannot see.',
    }
  }
  if (!row.is_current) {
    return {
      enabled: false,
      label: 'Ask for approval',
      reason: 'This version has been superseded. Ask about the current one instead.',
    }
  }
  if (!askable) {
    // The server's map says no and this screen does not know better. It reads
    // as unavailable rather than as an error, because a vocabulary that failed
    // to load lands here too.
    return { enabled: false, label: 'Ask for approval', reason: null }
  }
  return {
    enabled: true,
    // `rejected` is the one state where asking again is a *second* ask, and the
    // client is owed the distinction: they already told the firm no.
    label: row.approval_status === 'rejected' ? 'Ask again' : 'Ask for approval',
    reason:
      row.approval_status === 'rejected'
        ? 'The client rejected this. Asking again replaces their answer.'
        : null,
  }
}

/**
 * What the checklist row offers.
 *
 * A `pending` item has no decision to make — the firm is waiting for a file, and
 * offering "verify" on nothing is offering to verify nothing. `verified` is
 * settled and reopens only by a new upload.
 *
 * **A `rejected` item offers no decision either** *(C27, §10.19 item 8; UAT
 * report #4)*. It used to offer *Verify*, and the server always refused it:
 * `REQUEST_TRANSITIONS` has `rejected → received` only, because the file that
 * was sent back is the file that was wrong. What it is waiting for is a file —
 * an upload, or a document on the case linked to it.
 */
export function requestActions(row: DocumentRequestRow): Array<'verified' | 'rejected'> {
  return row.state === 'received' ? ['verified', 'rejected'] : []
}

/**
 * The checklist items a file on the case can answer — C27 (§10.19 item 8).
 *
 * Everything but `verified`: a link brings no new file, so it may not undo a
 * verification (`domain/documents.link_to_request`, which refuses it). An item
 * that has an answer already may take another; the old one is superseded, as
 * an upload against the item would supersede it. The file's own item is left
 * out — it already answers that one.
 */
export function linkableRequests(
  requests: DocumentRequestRow[],
  row: Pick<DocumentRow, 'request_id' | 'is_current' | 'case_id'>,
): DocumentRequestRow[] {
  if (!row.is_current || !row.case_id || row.request_id) return []
  return requests.filter((r) => r.state !== 'verified' && r.case_id === row.case_id)
}

/**
 * The guardrail's warnings, as the upload screen shows them.
 *
 * `checked: false` is **not** a warning and must not render as one: it means the
 * file could not be read, which is ordinary — a scan with no OCR, a format we do
 * not handle — and a screen that flagged it would train the firm to dismiss the
 * one that matters.
 */
export function guardrailNotice(result: UploadResult | null): {
  tone: 'none' | 'warning'
  warnings: GuardrailWarning[]
} {
  const warnings = result?.warnings ?? []
  return { tone: warnings.length ? 'warning' : 'none', warnings }
}

/** How many checklist items the firm is still waiting on — the number a case
 *  header shows. A rejected item counts: the firm is still waiting. */
export function outstandingCount(rows: DocumentRequestRow[]): number {
  return rows.filter((r) => r.state === 'pending' || r.state === 'rejected').length
}

/** `1.2 MB`. Bytes are what the server sends and nobody reads bytes. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
